import React, { useState, useEffect, useCallback } from 'react';
import { periodsApi } from '../../utils/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import './Admin.css';

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STATUS_BADGE = {
  OPEN:   { label: 'Open',   cls: 'adm-badge--open' },
  CLOSED: { label: 'Closed', cls: 'adm-badge--closed' },
  LOCKED: { label: 'Locked', cls: 'adm-badge--locked' },
};

/* ── Confirm modal for close / lock / reopen ─────────────────────────── */
function ConfirmModal({ title, message, danger, onConfirm, onCancel, saving }) {
  return (
    <div className="adm-modal-overlay">
      <div className="adm-modal" style={{ maxWidth: 420 }}>
        <div className="adm-modal-head"><h2>{title}</h2></div>
        <div className="adm-modal-body adm-confirm-body">{message}</div>
        <div className="adm-modal-foot">
          <button className="adm-btn adm-btn--ghost" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className={`adm-btn ${danger ? 'adm-btn--danger' : 'adm-btn--primary'}`} onClick={onConfirm} disabled={saving}>
            {saving ? <LoadingSpinner size="small" variant="button" /> : 'Confirm'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Create FY modal ─────────────────────────────────────────────────── */
function FiscalYearModal({ onClose }) {
  const [form, setForm] = useState({ code: '', name: '', startDate: '', endDate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      await periodsApi.createFiscalYear(form);
      onClose(true);
    } catch (e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="adm-modal-overlay">
      <div className="adm-modal">
        <div className="adm-modal-head">
          <h2>Create Fiscal Year</h2>
          <button className="adm-modal-close" onClick={() => onClose(false)}><i className="fas fa-times"></i></button>
        </div>
        <div className="adm-modal-body">
          {error && <div className="adm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
          <div className="adm-field">
            <label className="adm-label">Code *</label>
            <input className="adm-input" value={form.code} onChange={(e) => set('code', e.target.value)}
              placeholder="e.g. FY2026" autoFocus />
          </div>
          <div className="adm-field">
            <label className="adm-label">Name *</label>
            <input className="adm-input" value={form.name} onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Fiscal Year 2025–2026" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div className="adm-field">
              <label className="adm-label">Start Date *</label>
              <input className="adm-input" type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
            </div>
            <div className="adm-field">
              <label className="adm-label">End Date *</label>
              <input className="adm-input" type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="adm-modal-foot">
          <button className="adm-btn adm-btn--ghost" onClick={() => onClose(false)}>Cancel</button>
          <button className="adm-btn adm-btn--primary" onClick={submit} disabled={saving}>
            {saving ? <LoadingSpinner size="small" variant="button" /> : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Add period modal ─────────────────────────────────────────────────── */
function PeriodModal({ fiscalYear, onClose }) {
  const [form, setForm] = useState({ code: '', name: '', startDate: '', endDate: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true); setError(null);
    try {
      await periodsApi.addPeriod(fiscalYear.id, form);
      onClose(true);
    } catch (e) { setError(e.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="adm-modal-overlay">
      <div className="adm-modal">
        <div className="adm-modal-head">
          <h2>Add Period — {fiscalYear.name}</h2>
          <button className="adm-modal-close" onClick={() => onClose(false)}><i className="fas fa-times"></i></button>
        </div>
        <div className="adm-modal-body">
          {error && <div className="adm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
          <div className="adm-field">
            <label className="adm-label">Code *</label>
            <input className="adm-input" value={form.code} onChange={(e) => set('code', e.target.value)}
              placeholder="e.g. P01" autoFocus />
          </div>
          <div className="adm-field">
            <label className="adm-label">Name *</label>
            <input className="adm-input" value={form.name} onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. September 2025" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div className="adm-field">
              <label className="adm-label">Start Date *</label>
              <input className="adm-input" type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
            </div>
            <div className="adm-field">
              <label className="adm-label">End Date *</label>
              <input className="adm-input" type="date" value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />
            </div>
          </div>
        </div>
        <div className="adm-modal-foot">
          <button className="adm-btn adm-btn--ghost" onClick={() => onClose(false)}>Cancel</button>
          <button className="adm-btn adm-btn--primary" onClick={submit} disabled={saving}>
            {saving ? <LoadingSpinner size="small" variant="button" /> : 'Add Period'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Fiscal Year row (collapsible) ───────────────────────────────────── */
function FiscalYearRow({ fy, canManage, canClose, onAction, onAddPeriod }) {
  const [open, setOpen] = useState(true);
  const periods = fy.periods || [];

  return (
    <div className="adm-fy">
      <div className="adm-fy-header" onClick={() => setOpen((x) => !x)}>
        <i className={`fas fa-chevron-${open ? 'down' : 'right'} adm-fy-toggle`}></i>
        <span className="adm-fy-name">{fy.name}</span>
        <span className="adm-badge adm-badge--system" style={{ marginRight: '0.5rem' }}>{fy.code}</span>
        <span className="adm-fy-meta">{fmtDate(fy.startDate)} – {fmtDate(fy.endDate)}</span>
        <span className="adm-fy-meta" style={{ marginLeft: '0.75rem' }}>{periods.length} period{periods.length !== 1 ? 's' : ''}</span>
        {canManage && (
          <button className="adm-btn adm-btn--ghost adm-btn--sm" style={{ marginLeft: '0.75rem' }}
            title="Add Period"
            onClick={(e) => { e.stopPropagation(); onAddPeriod(fy); }}>
            <i className="fas fa-plus"></i> Add Period
          </button>
        )}
      </div>

      {open && (
        periods.length === 0
          ? <p style={{ padding: '1rem', color: 'var(--text-faint)', fontSize: 'var(--font-size-sm)' }}>No periods yet.</p>
          : (
            <table className="adm-periods-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Start</th>
                  <th>End</th>
                  <th>Status</th>
                  {(canManage || canClose) && <th style={{ textAlign: 'right' }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {periods.map((p) => {
                  const badge = STATUS_BADGE[p.status] || { label: p.status, cls: '' };
                  return (
                    <tr key={p.id}>
                      <td><code style={{ fontFamily: 'var(--font-family-mono)', fontSize: '0.82rem' }}>{p.code}</code></td>
                      <td>{p.name}</td>
                      <td style={{ color: 'var(--text-muted)' }}>{fmtDate(p.startDate)}</td>
                      <td style={{ color: 'var(--text-muted)' }}>{fmtDate(p.endDate)}</td>
                      <td><span className={`adm-badge ${badge.cls}`}>{badge.label}</span></td>
                      {(canManage || canClose) && (
                        <td>
                          <div className="adm-period-actions" style={{ justifyContent: 'flex-end' }}>
                            {canClose && p.status === 'OPEN' && (
                              <button className="adm-btn adm-btn--ghost adm-btn--sm"
                                onClick={() => onAction('close', p)}>
                                <i className="fas fa-lock-open"></i> Close
                              </button>
                            )}
                            {canClose && p.status === 'CLOSED' && (
                              <>
                                <button className="adm-btn adm-btn--danger adm-btn--sm"
                                  onClick={() => onAction('lock', p)}>
                                  <i className="fas fa-lock"></i> Lock
                                </button>
                                <button className="adm-btn adm-btn--ghost adm-btn--sm"
                                  onClick={() => onAction('reopen', p)}>
                                  <i className="fas fa-unlock"></i> Reopen
                                </button>
                              </>
                            )}
                            {canClose && p.status === 'LOCKED' && (
                              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-faint)' }}>
                                <i className="fas fa-lock"></i> Locked
                              </span>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
      )}
    </div>
  );
}

/* ── Main page ────────────────────────────────────────────────────────── */
const PeriodsPage = () => {
  const { hasPermission } = useAuth();
  const canManage = hasPermission('periods.manage');
  const canClose  = hasPermission('periods.close');

  const [fiscalYears, setFiscalYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null); // { action, period }
  const [actionSaving, setActionSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setFiscalYears(await periodsApi.listFiscalYears()); }
    catch (e) { setError(e.message || 'Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAction = (action, period) => setConfirm({ action, period });

  const doAction = async () => {
    const { action, period } = confirm;
    setActionSaving(true);
    try {
      if (action === 'close')  await periodsApi.closePeriod(period.id);
      if (action === 'lock')   await periodsApi.lockPeriod(period.id);
      if (action === 'reopen') await periodsApi.reopenPeriod(period.id);
      setConfirm(null);
      load();
    } catch (e) { setError(e.message || 'Action failed'); setConfirm(null); }
    finally { setActionSaving(false); }
  };

  const ACTION_LABELS = {
    close:  { title: 'Close Period', verb: 'close', danger: false },
    lock:   { title: 'Lock Period',  verb: 'lock — this prevents any further re-opening', danger: true },
    reopen: { title: 'Reopen Period', verb: 're-open', danger: false },
  };

  const closeFyModal = (refresh) => { setModal(null); if (refresh) load(); };

  return (
    <div className="adm-page">
      <div className="adm-header">
        <div>
          <h1 className="adm-title"><i className="fas fa-calendar-alt" style={{ color: 'var(--primary)', marginRight: '0.5rem' }}></i>Fiscal Periods</h1>
          <p className="adm-subtitle">Manage fiscal years and their accounting periods.</p>
        </div>
        {canManage && (
          <button className="adm-btn adm-btn--primary" onClick={() => setModal({ type: 'fy' })}>
            <i className="fas fa-plus"></i> New Fiscal Year
          </button>
        )}
      </div>

      {error && <div className="adm-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}

      {loading ? (
        <LoadingSpinner variant="default" message="Loading fiscal years…" />
      ) : fiscalYears.length === 0 ? (
        <div className="adm-info"><i className="fas fa-info-circle"></i> No fiscal years created yet.</div>
      ) : (
        fiscalYears.map((fy) => (
          <FiscalYearRow key={fy.id} fy={fy} canManage={canManage} canClose={canClose}
            onAction={handleAction}
            onAddPeriod={(fy) => setModal({ type: 'period', fy })} />
        ))
      )}

      {modal?.type === 'fy' && <FiscalYearModal onClose={closeFyModal} />}
      {modal?.type === 'period' && <PeriodModal fiscalYear={modal.fy} onClose={closeFyModal} />}

      {confirm && (() => {
        const meta = ACTION_LABELS[confirm.action] || {};
        return (
          <ConfirmModal
            title={meta.title}
            danger={meta.danger}
            saving={actionSaving}
            message={
              <>
                Are you sure you want to <strong>{meta.verb}</strong> period{' '}
                <strong>{confirm.period.name}</strong>?
                {confirm.action === 'lock' && (
                  <><br /><br /><span style={{ color: 'var(--danger-600)' }}>Warning: A locked period cannot be reopened.</span></>
                )}
              </>
            }
            onConfirm={doAction}
            onCancel={() => setConfirm(null)}
          />
        );
      })()}
    </div>
  );
};

export default PeriodsPage;
